import {useCallback, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useAuthClient} from '../../../../../utils/use-auth-client'
import {
  updateProposalInfo,
  resetDirty,
} from '../../../../../app-store/ProposalSlice'
import {PARTNER_VALUES} from '../../../../../accountManagement/features/AccountInfo/config/locationFieldRules'
import {useProposalPaymentPreview} from './useProposalPaymentPreview'
import {selectUserRoles} from '../../../../../app-store/selector'

// Recurly-seeded items carry configuration as a [{name, value}] array
// instead of the plain object the backend proposal schema expects — translate it before save.
function normalizeConfiguration(configuration) {
  return configuration.reduce((acc, {name, value}) => {
    acc[name] = value
    return acc
  }, {})
}

// Strips UI-only fields from options/items and maps coupons to the backend
// schema shape { couponCode, couponName } (only persists valid coupons).
export function sanitizeOptions(options) {
  return options.map(option => ({
    ...option,
    items: option.items.map(({alreadyIncluded, ...item}) => ({
      ...item,
      // Backend stores recurring_amount = quantity × unitPrice, so unitPrice
      // must be per-unit. Divide here; qty=1 items are unaffected.
      ...(item.unitPrice != null &&
        item.quantity > 1 && {
          unitPrice: item.unitPrice / item.quantity,
        }),
      ...(Array.isArray(item.configuration) && {
        configuration: normalizeConfiguration(item.configuration),
      }),
      ...(item.addons?.length && {
        addons: item.addons.map(
          // eslint-disable-next-line no-unused-vars
          ({alreadyIncluded: _ai, ...addon}) => addon,
        ),
      }),
    })),
    coupons: (option.coupons ?? [])
      .filter(c => c.isValid)
      .map(c => ({
        couponCode: c.code,
        ...(c.name && {couponName: c.name}),
      })),
  }))
}

// Only attribute the sale to a rep/partner/override-rep when the matching
// toggle actually selected that path — mirrors the salesRepInfo/xpCode/
// overrideRepInfo visibility rules in locationFieldRules.js, so a rep or XP
// Code left behind from a prior toggle choice never gets sent.
function buildSalesAttributionFields({
  salesRepOrPartner,
  salesRepInfo,
  xpCode,
  overrideSST,
  overrideRepInfo,
}) {
  return {
    salesRepOrPartner,
    ...(salesRepOrPartner === 'sales-rep' && {
      behalfRepId: salesRepInfo?.repInfo?.FederationIdentifier || '',
      behalfEmail: salesRepInfo?.repInfo?.Email || '',
      behalfFirstName: salesRepInfo?.repInfo?.FirstName || '',
      behalfLastName: salesRepInfo?.repInfo?.LastName || '',
    }),
    ...(PARTNER_VALUES.includes(salesRepOrPartner) && {
      xpCode: xpCode || '',
    }),
    overrideSST,
    ...(overrideSST === 'yes' && {
      overrideRepId: overrideRepInfo?.repInfo?.FederationIdentifier || '',
      overrideRepEmail: overrideRepInfo?.repInfo?.Email || '',
      overrideRepFirst: overrideRepInfo?.repInfo?.FirstName || '',
      overrideRepLast: overrideRepInfo?.repInfo?.LastName || '',
    }),
  }
}

export function useProposalSave() {
  const dispatch = useDispatch()
  const client = useAuthClient()
  const {refreshPaymentPreview} = useProposalPaymentPreview()
  const {actualRole} = useSelector(selectUserRoles)

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)

  const handleSave = useCallback(async () => {
    setSaving(true)
    setSaveError(null)

    try {
      const {
        proposalId,
        salesRepId,
        userRole,
        businessName,
        clientFirstName,
        clientLastName,
        clientEmail,
        clientPhone,
        country,
        clientType,
        thryvId,
        salesRepOrPartner,
        salesRepInfo,
        xpCode,
        overrideSST,
        overrideRepInfo,
        statusCode,
        expirationDate,
        sentAt,
        acceptedAt,
        declinedAt,
        feedback,
        repNotes,
        options,
        proposalLink,
      } = await refreshPaymentPreview()

      if (!proposalId) {
        const body = {
          repId: salesRepId,
          userRole:
            userRole || Array.isArray(actualRole) ? actualRole[0] : actualRole,
          businessName,
          country,
          clientType,
          ...buildSalesAttributionFields({
            salesRepOrPartner,
            salesRepInfo,
            xpCode,
            overrideSST,
            overrideRepInfo,
          }),
          ...(clientFirstName && {clientFirstName}),
          ...(clientLastName && {clientLastName}),
          ...(clientEmail && {clientEmail}),
          ...(clientPhone && {clientPhone}),
          ...(thryvId && {thryvId}),
          ...(expirationDate && {expirationDate}),
          ...(options?.length && {options: sanitizeOptions(options)}),
        }

        const response = await client('proposals', {data: body})
        dispatch(
          updateProposalInfo({
            proposalId: response.proposalId,
            proposalLink: response.proposalLink,
          }),
        )
        dispatch(resetDirty())
      } else {
        const body = {
          ...(businessName != null && {businessName}),
          ...(clientFirstName != null && {clientFirstName}),
          ...(clientLastName != null && {clientLastName}),
          ...(clientEmail != null && {clientEmail}),
          ...(clientPhone != null && {clientPhone}),
          ...(country != null && {country}),
          ...(clientType != null && {clientType}),
          ...(thryvId != null && {thryvId}),
          ...buildSalesAttributionFields({
            salesRepOrPartner,
            salesRepInfo,
            xpCode,
            overrideSST,
            overrideRepInfo,
          }),
          ...(statusCode != null && {statusCode}),
          ...(expirationDate != null && {expirationDate}),
          ...(sentAt != null && {sentAt}),
          ...(acceptedAt != null && {acceptedAt}),
          ...(declinedAt != null && {declinedAt}),
          ...(feedback != null && {feedback}),
          ...(repNotes != null && {repNotes}),
          ...(options?.length && {options: sanitizeOptions(options)}),
          ...(proposalLink !== null && {proposalLink}),
        }

        await client(`proposals/${proposalId}`, {data: body, method: 'PUT'})
        dispatch(resetDirty())
      }
    } catch (err) {
      setSaveError(err)
    } finally {
      setSaving(false)
    }
  }, [refreshPaymentPreview, client, dispatch])

  return {handleSave, saving, saveError}
}
